
import React, { useState, useEffect } from 'react';
import { useLeads, LeadStageType, ServiceType } from '../context/LeadContext';
import { usePartners } from '../context/PartnerContext';
import LeadCard from '../components/leads/LeadCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LeadReassignment } from '@/components/leads/LeadReassignment';
import { PageHeader } from '@/components/layouts/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Search,
  UserRound,
  Plus,
  Filter,
  Clock,
  CheckCircle,
  Info,
  Sparkles,
  ArrowUpRight
} from 'lucide-react';

export default function Leads() {
  const { leads, updateLeadStage } = useLeads();
  const { partners } = usePartners();
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<LeadStageType | 'all'>('all');
  const [serviceFilter, setServiceFilter] = useState<ServiceType | 'all'>('all');
  const [filteredLeads, setFilteredLeads] = useState(leads);

  useEffect(() => {
    let filtered = leads;

    if (search) {
      filtered = filtered.filter(lead =>
        lead.name.toLowerCase().includes(search.toLowerCase()) ||
        lead.phone.includes(search) ||
        lead.address.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (stageFilter !== 'all') {
      filtered = filtered.filter(lead => lead.stage === stageFilter);
    }

    if (serviceFilter !== 'all') {
      filtered = filtered.filter(lead => lead.service === serviceFilter);
    }

    // Sort by date, newest first
    filtered = [...filtered].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setFilteredLeads(filtered);
  }, [leads, search, stageFilter, serviceFilter]);

  const handleStageChange = (id: string, stage: LeadStageType) => {
    updateLeadStage(id, stage);
  };

  // Quick stats
  const newLeads = leads.filter(l => l.stage === 'Νέο').length;
  const pendingLeads = leads.filter(l => l.stage === 'σεΕκκρεμότητα').length;
  const successLeads = leads.filter(l => l.stage === 'έκλεισεΗΔουλειά').length;
  const infoLeads = leads.filter(l => l.stage === 'ήθελεΠληροφορίες').length;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <PageHeader
        title="Ενδιαφερόμενοι"
        description="Διαχείριση επαφών και leads"
        icon={<UserRound />}
        iconGradient="purple"
        actions={
          <>
            <Link to="/admin/leads/statistics">
              <Button variant="outline" className="border-slate-200">
                <ArrowUpRight className="mr-2 h-4 w-4" /> Στατιστικά
              </Button>
            </Link>
            <Link to="/admin/leads/assignment">
              <Button variant="gradient">
                <Plus className="mr-2 h-4 w-4" /> Ανάθεση Leads
              </Button>
            </Link>
          </>
        }
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          value={newLeads}
          title="Νέα"
          icon={<Sparkles />}
          variant="gradient"
          gradientColors="from-blue-50 to-indigo-50"
          size="compact"
        />
        <StatCard
          value={pendingLeads}
          title="Σε Αναμονή"
          icon={<Clock />}
          variant="gradient"
          gradientColors="from-amber-50 to-orange-50"
          size="compact"
        />
        <StatCard
          value={successLeads}
          title="Επιτυχίες"
          icon={<CheckCircle />}
          variant="gradient"
          gradientColors="from-emerald-50 to-teal-50"
          size="compact"
        />
        <StatCard
          value={infoLeads}
          title="Πληροφορίες"
          icon={<Info />}
          variant="gradient"
          gradientColors="from-slate-50 to-slate-100"
          size="compact"
        />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Leads List - Takes 3 columns */}
        <div className="xl:col-span-3 space-y-6">
          {/* Filters */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Αναζήτηση ονόματος, τηλεφώνου..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-slate-50 border-slate-200"
                  />
                </div>
                <Select onValueChange={(value) => setStageFilter(value === 'all' ? 'all' : value as LeadStageType)}>
                  <SelectTrigger className="w-full md:w-48 bg-slate-50 border-slate-200">
                    <Filter className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Στάδιο" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλα τα Στάδια</SelectItem>
                    <SelectItem value="Νέο">Νέο</SelectItem>
                    <SelectItem value="σεΕκκρεμότητα">Σε Εκκρεμότητα</SelectItem>
                    <SelectItem value="έκλεισεΗΔουλειά">Έκλεισε η Δουλειά</SelectItem>
                    <SelectItem value="ήθελεΠληροφορίες">Ήθελε Πληροφορίες</SelectItem>
                  </SelectContent>
                </Select>
                <Select onValueChange={(value) => setServiceFilter(value === 'all' ? 'all' : value as ServiceType)}>
                  <SelectTrigger className="w-full md:w-48 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Υπηρεσία" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλες οι Υπηρεσίες</SelectItem>
                    <SelectItem value="εξοικονομώ">Εξοικονομώ</SelectItem>
                    <SelectItem value="αντλίεςΘερμότητας">Αντλίες Θερμότητας</SelectItem>
                    <SelectItem value="φωτοβολταϊκά">Φωτοβολταϊκά</SelectItem>
                    <SelectItem value="κλιματιστικά">Κλιματιστικά</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Results count */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Βρέθηκαν <span className="font-semibold text-slate-700">{filteredLeads.length}</span> επαφές
            </p>
            {(search || stageFilter !== 'all' || serviceFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setStageFilter('all');
                  setServiceFilter('all');
                }}
                className="text-blue-600 hover:text-blue-700"
              >
                Καθαρισμός φίλτρων
              </Button>
            )}
          </div>

          {/* Leads list */}
          {filteredLeads.length === 0 ? (
            <Card className="border-2 border-dashed border-slate-200 bg-slate-50">
              <CardContent className="py-16 text-center">
                <UserRound className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">Δεν βρέθηκαν επαφές</p>
                <p className="text-sm text-slate-400 mt-1">Δοκιμάστε διαφορετικά φίλτρα</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredLeads.map((lead, index) => (
                <div
                  key={lead.id}
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  <LeadCard
                    lead={lead}
                    onSendSMS={() => { }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar - Takes 1 column */}
        <div className="xl:col-span-1 space-y-4">
          <LeadReassignment />
        </div>
      </div>
    </div>
  );
}
